import React, { createContext, useState, useContext, useCallback, useEffect } from 'react';
import config from '../config';
import { getUserIdFromUrl } from '../helper/apiHelper';
import { useMaintenanceMode } from './MaintenanceContext';
import { useStatic } from './StaticContext';

// Context oluştur
const BonusContext = createContext();

// LOCAL DEMO: backend olmadan görselsiz tasarımı test edebilmek için
// localhost'ta sahte bonus verisi enjekte edilir. Production'da hiç çalışmaz.
const IS_LOCAL_DEMO = typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

const MOCK_BONUS_DATA = {
  full_name: 'Demo Kullanıcı',
  talep_data: [
    {
      bonus_type: 'Tümü, Spor',
      bonus_gorsel: '/static/image/long.webp',
      data: [{
        bonus_name: '%20 Kolay Çevrimli Spor Yatırım Bonusu',
        process_name: 'demo-spor-yatirim',
        kurallar: [{ title: 'Şartlar', metin: 'Min. 100 TL yatırım şartı.' }],
        is_bakim: false,
        talep_text: null,
        redirect_link: null,
      }],
    },
    {
      bonus_type: 'Tümü',
      bonus_gorsel: '/static/image/long.webp',
      data: [{
        bonus_name: 'İlk Yatırıma Özel %100 İade Bonusu',
        process_name: 'demo-ilk-yatirim',
        kurallar: [],
        is_bakim: false,
        talep_text: null,
        redirect_link: null,
      }],
    },
    {
      bonus_type: 'Tümü, Yatırım',
      bonus_gorsel: '/static/image/long.webp',
      data: [{
        bonus_name: '1000TL Yatırıma 1000TL Bonus',
        process_name: 'demo-1000-1000',
        kurallar: [],
        is_bakim: false,
        talep_text: null,
        redirect_link: null,
      }],
    },
    {
      bonus_type: 'Tümü, Deneme',
      bonus_gorsel: '/static/image/long.webp',
      data: [{
        bonus_name: 'Telegram Deneme Bonusu',
        process_name: 'demo-telegram-deneme',
        kurallar: [{ title: 'Şartlar', metin: 'Telegram kanalına katılım gerekir.' }],
        is_bakim: false,
        talep_text: null,
        redirect_link: null,
      }],
    },
    {
      bonus_type: 'Tümü, Hoşgeldin, Casino',
      bonus_gorsel: '/static/image/long.webp',
      data: [{
        bonus_name: '10.000₺ Casino Hoşgeldin Bonusu',
        process_name: 'demo-casino-hosgeldin',
        kurallar: [],
        is_bakim: false,
        talep_text: null,
        redirect_link: null,
      }],
    },
    {
      bonus_type: 'Tümü, Casino',
      bonus_gorsel: '/static/image/long.webp',
      data: [{
        bonus_name: '%25 Gecelik Slot Çevrim Bonusu',
        process_name: 'demo-gece-slot',
        kurallar: [],
        is_bakim: false,
        talep_text: null,
        redirect_link: null,
      }],
    },
    {
      bonus_type: 'Tümü, Kayıp',
      bonus_gorsel: '/static/image/long.webp',
      data: [{
        bonus_name: 'Haftalık Casino Kayıp Bonusu',
        process_name: 'demo-haftalik-kayip',
        kurallar: [{ title: 'Şartlar', metin: 'Haftalık net kayıp üzerinden.' }],
        is_bakim: false,
        talep_text: null,
        redirect_link: null,
      }],
    },
    {
      bonus_type: 'Tümü, Çark',
      bonus_gorsel: '/static/image/long.webp',
      data: [{
        bonus_name: 'Günlük Şans Çarkı',
        process_name: 'demo-cark',
        kurallar: [],
        is_bakim: false,
        talep_text: null,
        redirect_link: null,
      }],
    },
  ],
  bonus_type_tabs: ['Tümü', 'Spor', 'Casino', 'Hoşgeldin', 'Çark', 'Kayıp', 'Yatırım', 'Deneme'],
};

// Bonus verisini sağlayacak context provider
export const BonusProvider = ({ children }) => {
  const [bonusData, setBonusData] = useState([]);
  const [tabs, setTabs] = useState([]);
  const [gamesData, setGamesData] = useState([]);
  const [gamesTabs, setGamesTabs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [gamesLoading, setGamesLoading] = useState(true);
  const [error, setError] = useState(null);
  const [gamesError, setGamesError] = useState(null);
  const userId = getUserIdFromUrl();
  const [userName, setUserName] = useState('');
  const { isMaintenanceMode } = useMaintenanceMode(); // Bakım modu bilgisini al
  const { isLanguage, selectedLanguage } = useStatic(); // Dil ayarlarını al


  const fetchBonusForUser = useCallback(async () => {
    // LOCAL DEMO: backend yok → mock data ile görselsiz tasarımı göster
    if (IS_LOCAL_DEMO) {
      setUserName(MOCK_BONUS_DATA.full_name);
      setBonusData(MOCK_BONUS_DATA.talep_data);
      setTabs(MOCK_BONUS_DATA.bonus_type_tabs);
      setError(null);
      setLoading(false);
      return;
    }

    // Önce userId kontrolü yap
    if (!userId) {
      setError("invalid_access");
      setLoading(false);
      return;
    }

    try {
      // İstek payload'ını oluştur
      const payload = {
        process_id: "getc-bonuses",
        user_id: userId,
        recaptcha: "test",
      };

      // Bakım modu kontrolü
      if (isMaintenanceMode) {
        payload.dev = "10";
      }

      // Dil desteği kontrolü
      if (isLanguage && selectedLanguage) {
        payload.lang = selectedLanguage;
      }

      let body = JSON.stringify(payload);
      let keys = await window.rh.cr(body);
      
      const response = await fetch(`${config.apiBaseUrl}`, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json", 
          "hk": keys[0],
          "hh": keys[1]
        },
        body: body,
      });

      // 403 hatası kontrolü
      if (response.status === 403) {
        setError("user_blocked");
        setLoading(false);
        return;
      }

      const result = await response.json();

      if (result.status) {
        setUserName(result.full_name);
        setBonusData(result.data.talep_data);
        setTabs(result.data.bonus_type_tabs);
        setError(null);
        setLoading(false);
      } else if (result.message === "Bilinmeyen kullanıcı") {
        setError("user_not_found");
        setLoading(false);
      } else {
        setError("api_error");
        setLoading(false);
      }
    } catch (error) {
      console.error("İstek sırasında hata oluştu:", error);
      setError("connection_error");
      setLoading(false);
    }
  }, [userId, isMaintenanceMode, isLanguage, selectedLanguage]);

  const fetchGamesForUser = useCallback(async () => {
    // LOCAL DEMO: backend yok → mock games (boş set) ile graceful başlat
    if (IS_LOCAL_DEMO) {
      setGamesData([]);
      setGamesTabs([]);
      setGamesError(null);
      setGamesLoading(false);
      return;
    }

    // Önce userId kontrolü yap
    if (!userId) {
      setGamesError("invalid_access");
      setGamesLoading(false);
      return;
    }

    try {
      // İstek payload'ını oluştur
      const payload = {
        process_id: "getc-games",
        user_id: userId,
        recaptcha: "test",
      };

      // Bakım modu kontrolü
      if (isMaintenanceMode) {
        payload.dev = "10";
      }

      // Dil desteği kontrolü
      if (isLanguage && selectedLanguage) {
        payload.lang = selectedLanguage;
      }

      let body = JSON.stringify(payload);
      let keys = await window.rh.cr(body);
      
      const response = await fetch(`${config.apiBaseUrl}`, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json", 
          "hk": keys[0],
          "hh": keys[1]
        },
        body: body,
      });

      // 403 hatası kontrolü
      if (response.status === 403) {
        setGamesError("user_blocked");
        setGamesLoading(false);
        return;
      }

      const result = await response.json();

      if (result.status) {
        setGamesData(result.data.talep_data);
        setGamesTabs(result.data.bonus_type_tabs || []);
        setGamesError(null);
        setGamesLoading(false);
      } else if (result.message === "Bilinmeyen kullanıcı") {
        setGamesError("user_not_found");
        setGamesLoading(false);
      } else {
        setGamesError("api_error");
        setGamesLoading(false);
      }
    } catch (error) {
      console.error("Oyunlar API isteği sırasında hata oluştu:", error);
      setGamesError("connection_error");
      setGamesLoading(false);
    }
  }, [userId, isMaintenanceMode, isLanguage, selectedLanguage]);

  // Bonus verisi, loading durumu ve hata bilgisini sağlayan context
  return (
    <BonusContext.Provider value={{ 
      bonusData, 
      loading, 
      error, 
      tabs, 
      fetchBonusForUser, 
      gamesData,
      gamesLoading,
      gamesError,
      gamesTabs,
      fetchGamesForUser,
      userName, 
      userId 
    }}>
      {children}
    </BonusContext.Provider>
  );
};

// Context'teki veriye erişmek için kullanılacak özel hook
export const useBonus = () => useContext(BonusContext);